import React, { useEffect, useState } from 'react';
import { Formik, Field, Form } from 'formik';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../store/reducers/userSlice';
import { loginBackend } from '../../api/auth';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { isUnverifiedSession } from '../../utils/verification';

const LoginPage = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const LoginSchema = Yup.object().shape({
    password: Yup.string().required('Password is required!'),
    phone_number: Yup.string().required('Phone number is required!'),
  });

  const from = location.state?.from || '/';

  const handleSubmit = async (values) => {
    setIsLoading(true);
    loginBackend(values.phone_number, values.password)
      .then((data) => {
        setIsLoading(false);
        localStorage.setItem('user', JSON.stringify(data));
        dispatch(login(data));
        // A rejected account signs in only to re-submit its documents (#145).
        // The session is kept because that PATCH needs it, but ProtectedRoutes
        // keeps every shop route out of reach.
        if (isUnverifiedSession(data)) {
          navigate('/reverify', { replace: true });
          return;
        }
        toast.success("Login successful");
        navigate(from, { replace: true });
      })
      .catch((error) => {
        setIsLoading(false);
        // Backend flags unverified accounts with a dedicated code —
        // send them to the pending-approval page instead of a dead-end toast.
        // Rejected accounts land on the same page with the staff reason.
        const code = error?.data?.code;
        if (code === 'ACCOUNT_UNVERIFIED' || code === 'ACCOUNT_REJECTED') {
          navigate('/pending-approval', {
            state: {
              from: 'login',
              rejected: code === 'ACCOUNT_REJECTED',
              note: error?.data?.verification_note || '',
            },
          });
          return;
        }
        toast.error(error?.message || 'An error occurred');
      });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-black">Login</h2>
            <p className="text-sm md:text-base text-gray-500 mt-2 mb-8 sm:mb-10">
              Login with your phone number and password
            </p>
          </div>

          <Formik
            validationSchema={LoginSchema}
            initialValues={{
              password: '',
              phone_number: '',
            }}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className="flex flex-col justify-center">
                <div className="grid grid-cols-1 gap-5">
                  {/* Phone Number Field */}
                  <div className="grid">
                    <label htmlFor="phone_number" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {/* Phone icon */}
                      </div>
                      <Field
                        className="py-2 pl-10 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                        id="phone_number"
                        name="phone_number"
                        placeholder="Phone Number"
                        autoComplete="tel"
                        type="text"
                      />
                    </div>
                    {errors.phone_number && touched.phone_number && (
                      <span className="text-red-400 text-sm mt-2">
                        {errors.phone_number}
                      </span>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="grid">
                    <label htmlFor="password" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {/* Lock icon */}
                      </div>
                      <Field
                        className="py-2 pl-10 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-brand-500 h-11 md:h-12"
                        id="password"
                        name="password"
                        placeholder="Password"
                        type="password"
                      />
                    </div>
                    {errors.password && touched.password && (
                      <span className="text-red-400 text-sm mt-2">
                        {errors.password}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <Link to="/forgot-password" className="text-sm text-brand-600 hover:text-brand-700">
                      Forgot Password?
                    </Link>
                  </div>

                  <Button type="submit" size="lg" fullWidth disabled={isLoading} className="my-1">
                    {!isLoading ? 'Login' : 'Logging in...'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>

          {/* Login as Seller Button */}
          <div className="mt-6">
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              onClick={() => window.open('https://dashboard.wholesaledai.com/auth/login', '_blank')}
            >
              Login as Seller
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-brand-600 hover:text-brand-700">
                Register
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;